import {
    Directive,
    ElementRef,
    Input,
    OnInit,
    OnDestroy,
    ComponentFactoryResolver,
    ViewContainerRef,
    ComponentRef,
    Renderer2,
    Injector
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';
import { DictationButtonComponent } from '@app/shared/components/dictation-button/dictation-button.component';

@Directive({
    selector: '[dictationEnabled]'
})
export class DictationDirective implements OnInit, OnDestroy {
    @Input() dictationContext?: string;
    @Input() dictationPatientId?: string;
    @Input() dictationComponentName?: string;

    private dictationButtonRef?: ComponentRef<DictationButtonComponent>;
    private containerElement?: HTMLElement;
    private formControl?: FormControl;

    constructor(
        private elementRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
        private componentFactoryResolver: ComponentFactoryResolver,
        private viewContainerRef: ViewContainerRef,
        private renderer: Renderer2,
        private injector: Injector,
        private ngControl?: NgControl
    ) { }

    ngOnInit() {
        this.setupDictation();
    }

    ngOnDestroy() {
        if (this.dictationButtonRef) {
            this.dictationButtonRef.destroy();
        }
    }

    private setupDictation() {
        // Get the form control
        this.formControl = this.ngControl?.control as FormControl;

        // Detect container type and setup accordingly
        const inputElement = this.elementRef.nativeElement;
        const containerType = this.detectContainerType(inputElement);

        switch (containerType) {
            case 'mat-form-field':
                this.setupMatFormFieldDictation();
                break;
            case 'standalone':
                this.setupStandaloneDictation();
                break;
            default:
                this.setupStandaloneDictation();
        }
    }

    private detectContainerType(element: HTMLElement): 'mat-form-field' | 'standalone' {
        // Check if element is inside a mat-form-field
        let parent = element.parentElement;
        while (parent) {
            if (parent.classList.contains('mat-form-field') ||
                parent.tagName.toLowerCase() === 'mat-form-field') {
                return 'mat-form-field';
            }
            parent = parent.parentElement;
            // Don't go too far up the DOM
            if (parent === document.body) break;
        }
        return 'standalone';
    }

    private setupMatFormFieldDictation() {
        // For mat-form-field, use matSuffix approach
        const matFormField = this.findMatFormField();
        if (matFormField) {
            this.createDictationButton('mat-suffix');
        } else {
            // Fallback to standalone if mat-form-field not found
            this.setupStandaloneDictation();
        }
    }

    private setupStandaloneDictation() {
        // For standalone inputs, use overlay approach
        this.createDictationButton('overlay');
    }

    private findMatFormField(): HTMLElement | null {
        let parent = this.elementRef.nativeElement.parentElement;
        while (parent) {
            if (parent.classList.contains('mat-form-field') ||
                parent.tagName.toLowerCase() === 'mat-form-field') {
                return parent;
            }
            parent = parent.parentElement;
            if (parent === document.body) break;
        }
        return null;
    }

    private createDictationButton(mode: 'mat-suffix' | 'overlay') {
        // Create the component
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(DictationButtonComponent);
        this.dictationButtonRef = this.viewContainerRef.createComponent(componentFactory);

        // Configure the component
        const dictationComponent = this.dictationButtonRef.instance;
        dictationComponent.targetControl = this.formControl;
        dictationComponent.patientId = this.dictationPatientId;
        dictationComponent.fieldContext = this.dictationContext || this.getDefaultContext();
        dictationComponent.componentName = this.dictationComponentName || this.getDefaultComponentName();
        dictationComponent.formFieldName = this.getFieldName();

        // Setup event handlers
        dictationComponent.textReceived.subscribe((text: string) => {
            this.onDictationReceived(text);
        });

        dictationComponent.recordingStarted.subscribe(() => {
            this.onRecordingStarted();
        });

        dictationComponent.recordingStopped.subscribe(() => {
            this.onRecordingStopped();
        });

        // Position the button based on mode
        if (mode === 'mat-suffix') {
            this.positionAsMatSuffix();
        } else {
            this.positionAsOverlay();
        }
    }

    private positionAsMatSuffix() {
        // Add matSuffix class to the button element
        const buttonElement = this.dictationButtonRef?.location.nativeElement;
        if (buttonElement) {
            this.renderer.addClass(buttonElement, 'mat-suffix');

            // Find the mat-form-field and add the button as a direct child
            // This should make Angular Material position it correctly
            const matFormField = this.findMatFormField();
            if (matFormField) {
                // Insert the button right after the textarea/input element
                const inputElement = this.elementRef.nativeElement;
                if (inputElement.nextSibling) {
                    this.renderer.insertBefore(matFormField, buttonElement, inputElement.nextSibling);
                } else {
                    this.renderer.appendChild(matFormField, buttonElement);
                }
            }
        }
    }

    private positionAsOverlay() {
        const inputElement = this.elementRef.nativeElement;
        const buttonElement = this.dictationButtonRef?.location.nativeElement;

        if (buttonElement && inputElement) {
            // Create a positioned container if the input doesn't have one
            const container = this.ensurePositionedContainer(inputElement);

            // Style the input to make room for the button
            this.renderer.addClass(inputElement, 'dictation-enabled-input');
            this.renderer.setStyle(inputElement, 'padding-left', '48px');

            // Position the button as an overlay
            this.renderer.addClass(buttonElement, 'dictation-directive-button');
            this.renderer.setStyle(buttonElement, 'position', 'absolute');
            this.renderer.setStyle(buttonElement, 'left', '12px');
            this.renderer.setStyle(buttonElement, 'top', '50%');
            this.renderer.setStyle(buttonElement, 'transform', 'translateY(-50%)');
            this.renderer.setStyle(buttonElement, 'z-index', '10');
            this.renderer.setStyle(buttonElement, 'pointer-events', 'auto');

            // Add to container
            this.renderer.appendChild(container, buttonElement);
        }
    }

    private ensurePositionedContainer(inputElement: HTMLElement): HTMLElement {
        const parent = inputElement.parentElement;
        if (parent) {
            // Check if parent already has relative positioning
            const position = window.getComputedStyle(parent).position;
            if (position === 'relative' || position === 'absolute') {
                return parent;
            }

            // Add relative positioning to parent
            this.renderer.setStyle(parent, 'position', 'relative');
            return parent;
        }
        return inputElement;
    }

    private getDefaultContext(): string {
        const element = this.elementRef.nativeElement;
        const placeholder = element.placeholder;
        const id = element.id;
        const name = element.getAttribute('name');

        return placeholder || id || name || 'Text Input';
    }

    private getDefaultComponentName(): string {
        // Try to infer component name from the DOM structure
        let parent = this.elementRef.nativeElement.parentElement;
        while (parent) {
            const tagName = parent.tagName.toLowerCase();
            if (tagName.includes('component') || parent.classList.length > 0) {
                return tagName;
            }
            parent = parent.parentElement;
            if (parent === document.body) break;
        }
        return 'DictationDirective';
    }

    private getFieldName(): string {
        const element = this.elementRef.nativeElement;
        const formControlName = element.getAttribute('formControlName');
        const name = element.getAttribute('name');
        const id = element.id;

        return formControlName || name || id || 'unknown';
    }

    private onDictationReceived(text: string) {
        console.log(`📝 Dictation received via directive: ${text}`);
    }

    private onRecordingStarted() {
        console.log('🎤 Started dictating via directive');
    }

    private onRecordingStopped() {
        console.log('⏹️ Stopped dictating via directive');
    }
}